import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { toChangeRequests } from '@/domain/change-requests'
import type { ChangeRequest } from '@/domain/workflow.types'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import { type ChangeRequestDTO, workOrderApi } from '@/services/work-order.service'
import { useUsers } from './useUsers'
import { invalidateWorkOrderActivity } from './useWorkOrderTimeline'

/** React Query hooks for a work order's change requests (dynamic `work_order_checklist` instances). */

export const changeRequestsKey = (workOrderId: string) => ['change-requests', workOrderId] as const

const useChangeRequestsQuery = (workOrderId: string, enabled: boolean) =>
	useQuery({
		queryKey: changeRequestsKey(workOrderId),
		queryFn: () => workOrderApi.listChangeRequests(workOrderId),
		enabled,
	})

/** Every change request on the order with its author's display name, `[]` while loading. */
export function useChangeRequests(
	workOrderId: string,
	enabled = true,
): { changeRequests: ChangeRequest[]; isLoading: boolean; isSuccess: boolean; dataUpdatedAt: number } {
	const query = useChangeRequestsQuery(workOrderId, enabled)
	const { data: users } = useUsers()
	const changeRequests = useMemo(() => {
		const names = new Map((users ?? []).map((u) => [u.userId, u.displayName]))
		return toChangeRequests(query.data ?? [], (userId) => names.get(userId))
	}, [query.data, users])
	return { changeRequests, isLoading: query.isLoading, isSuccess: query.isSuccess, dataUpdatedAt: query.dataUpdatedAt }
}

type Snapshot = { previous: ChangeRequestDTO[] | undefined }

/** Cancels, snapshots, and patches the list — the shared `onMutate` of every optimistic change-request write. */
async function patchList(
	qc: QueryClient,
	workOrderId: string,
	patch: (list: ChangeRequestDTO[]) => ChangeRequestDTO[],
): Promise<Snapshot> {
	const key = changeRequestsKey(workOrderId)
	await qc.cancelQueries({ queryKey: key })
	const previous = qc.getQueryData<ChangeRequestDTO[]>(key)
	if (previous) qc.setQueryData<ChangeRequestDTO[]>(key, patch(previous))
	return { previous }
}

const patchOne = (id: string, update: (cr: ChangeRequestDTO) => ChangeRequestDTO) => (list: ChangeRequestDTO[]) =>
	list.map((cr) => (cr.instanceId === id ? update(cr) : cr))

/** Shared by every change-request write, so each can tell whether another on the same order is still in flight. */
const changeRequestMutationKey = (workOrderId: string) => ['change-request-write', workOrderId] as const

/**
 * Refetches only once the LAST in-flight write settles: an earlier write's
 * refetch can reach the server before a later write commits, and its stale
 * response would overwrite that later write's optimistic patch until the
 * next refetch put it back.
 */
const invalidateChangeRequests = (qc: QueryClient, workOrderId: string) => {
	if (qc.isMutating({ mutationKey: changeRequestMutationKey(workOrderId) }) > 1) return
	void qc.invalidateQueries({ queryKey: changeRequestsKey(workOrderId) })
	invalidateWorkOrderActivity(qc, workOrderId)
}

/** Restores the snapshot unconditionally and reports the failure; `failure` names the action for the user. */
function optimisticHandlers(qc: QueryClient, workOrderId: string, failure: string) {
	return {
		mutationKey: changeRequestMutationKey(workOrderId),
		onError: (error: unknown, _vars: unknown, ctx: Snapshot | undefined) => {
			if (ctx?.previous) qc.setQueryData(changeRequestsKey(workOrderId), ctx.previous)
			notification.error(apiErrorMessage(error, failure))
		},
		onSettled: () => invalidateChangeRequests(qc, workOrderId),
	}
}

/** A create has nothing to patch — the server assigns the id — so it only invalidates. */
export const useCreateChangeRequest = (workOrderId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationKey: changeRequestMutationKey(workOrderId),
		mutationFn: (itemLabels: string[]) => workOrderApi.createChangeRequest(workOrderId, itemLabels),
		onError: (error) => notification.error(apiErrorMessage(error, 'The change request could not be raised.')),
		onSettled: () => invalidateChangeRequests(qc, workOrderId),
	})
}

type ToggleVars = { changeRequestId: string; itemId: string; checked: boolean }

// ponytail: a failed toggle restores the whole-list snapshot, which can briefly
// revert a concurrent click on another item until `onSettled` refetches.
export const useToggleChangeRequestItem = (workOrderId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ changeRequestId, itemId, checked }: ToggleVars) =>
			workOrderApi.toggleChecklistInstanceItem(changeRequestId, itemId, checked),
		onMutate: ({ changeRequestId, itemId, checked }: ToggleVars) =>
			patchList(
				qc,
				workOrderId,
				patchOne(changeRequestId, (cr) => ({
					...cr,
					items: cr.items.map((item) => (item.itemId === itemId ? { ...item, checked } : item)),
				})),
			),
		...optimisticHandlers(qc, workOrderId, 'The change-request item could not be updated.'),
	})
}

type VerifyVars = { changeRequestId: string; itemId: string; verified: boolean }

/** The reviewer's tick, separate from the worker's done tick ({@link useToggleChangeRequestItem}); un-verifying clears the done tick too. */
export const useVerifyChangeRequestItem = (workOrderId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ changeRequestId, itemId, verified }: VerifyVars) =>
			workOrderApi.verifyChangeRequestItem(changeRequestId, itemId, verified),
		onMutate: ({ changeRequestId, itemId, verified }: VerifyVars) =>
			patchList(
				qc,
				workOrderId,
				patchOne(changeRequestId, (cr) => ({
					...cr,
					items: cr.items.map((item) =>
						item.itemId === itemId ? { ...item, verified, checked: verified && item.checked } : item,
					),
				})),
			),
		...optimisticHandlers(qc, workOrderId, 'The change-request item could not be verified.'),
	})
}

type RemoveItemVars = { changeRequestId: string; itemId: string }

export const useRemoveChangeRequestItem = (workOrderId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ changeRequestId, itemId }: RemoveItemVars) =>
			workOrderApi.removeChangeRequestItem(changeRequestId, itemId),
		onMutate: ({ changeRequestId, itemId }: RemoveItemVars) =>
			patchList(
				qc,
				workOrderId,
				patchOne(changeRequestId, (cr) => ({ ...cr, items: cr.items.filter((item) => item.itemId !== itemId) })),
			),
		...optimisticHandlers(qc, workOrderId, 'The item could not be removed.'),
	})
}

export const useDeleteChangeRequest = (workOrderId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (changeRequestId: string) => workOrderApi.deleteChangeRequest(changeRequestId),
		onMutate: (changeRequestId: string) =>
			patchList(qc, workOrderId, (list) => list.filter((cr) => cr.instanceId !== changeRequestId)),
		...optimisticHandlers(qc, workOrderId, 'The change request could not be deleted.'),
	})
}
